import React, { useEffect, useState } from 'react';
import { AppUser, Empresa } from '../types';
import { createUser, getUsers } from '../services/users.service';
import { getEmpresas } from '../services/empresas.service';
import { getErrorMessage } from '../utils/errorHandler';

const UsuariosPage: React.FC = () => {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'SUPERVISOR' | 'AGENTE'>('AGENTE');
  const [empresaId, setEmpresaId] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const [usersResp, empresasResp] = await Promise.all([getUsers(), getEmpresas()]);
      setUsers(usersResp.data);
      setEmpresas(empresasResp.data);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void cargar();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createUser({
        email,
        password,
        nombre,
        role,
        empresaId: empresaId || undefined,
      });
      setEmail('');
      setPassword('');
      setNombre('');
      setRole('AGENTE');
      setEmpresaId('');
      await cargar();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const getEmpresaLabel = (id?: string) => {
    if (!id) return '-';
    const empresa = empresas.find((item) => item._id === id);
    return empresa ? empresa.razonSocial : id;
  };

  return (
    <>
      <h1 className="page-title">Usuarios</h1>

      <div className="card">
        <h2 className="card-title">Crear usuario</h2>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Contraseña</label>
              <input
                type="password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Nombre</label>
              <input value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Rol</label>
              <select value={role} onChange={(e) => setRole(e.target.value as 'ADMIN' | 'SUPERVISOR' | 'AGENTE')}>
                <option value="ADMIN">ADMIN</option>
                <option value="SUPERVISOR">SUPERVISOR</option>
                <option value="AGENTE">AGENTE</option>
              </select>
            </div>
            <div className="form-group">
              <label>Empresa (opcional)</label>
              <select value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
                <option value="">Sin empresa</option>
                {empresas.map((empresa) => (
                  <option key={empresa._id} value={empresa._id}>
                    {empresa.razonSocial}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creando...' : 'Crear usuario'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Listado de usuarios</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : users.length === 0 ? (
          <div className="empty-state">No hay usuarios registrados.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Empresa</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id}>
                    <td>{user.nombre}</td>
                    <td>{user.email}</td>
                    <td>{user.role}</td>
                    <td>{getEmpresaLabel(user.empresaId)}</td>
                    <td>
                      <span className={`badge ${user.activo ? 'badge-success' : 'badge-secondary'}`}>
                        {user.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default UsuariosPage;
